import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Dialog as MuiDialog,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  PrimaryButton,
  SecondaryButton,
  PRIMARY_COLOR,
} from "../../FIgmaTemplateData/Create-Edit-Page/styles";
import { GfFigmaTemplateData } from "@swagger/typescript-fetch-goflyer";
import { useFigmaTemplateSelection } from "../../../../hooks/useFigmaTemplateSelection";
import TemplateDataCard from "../../../../components/TemplateDataCard";
import InfiniteScroll from "react-infinite-scroll-component";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";

export interface TemplateSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (template: GfFigmaTemplateData) => void;
  generating?: boolean;
  /** Store IDs from flyerItemList to prioritize specific templates */
  storeIds?: string[];
}

export const TemplateSelectionModal: React.FC<TemplateSelectionModalProps> = ({
  open,
  onClose,
  onGenerate,
  generating,
  storeIds,
}) => {
  const { t } = useTranslation();
  const { items, loading, hasMore, loadMore, selected, setSelected } =
    useFigmaTemplateSelection({
      pageSize: 24,
      storeIds,
      relations: ["image", "category", "figmaTemplateTag"],
    });
  const scrollParentRef = useRef<HTMLDivElement | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | undefined>();
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleSelect = (tpl: GfFigmaTemplateData) => {
    if (selected?.id === tpl.id) {
      setSelected(undefined);
    } else {
      setSelected(tpl);
    }
  };

  const openPreview = (src?: string) => {
    if (!src) return;
    setPreviewSrc(src);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewSrc(undefined);
  };

  const renderCard = (tpl: GfFigmaTemplateData) => (
    <TemplateDataCard
      key={tpl.id}
      template={tpl}
      isSelected={selected?.id === tpl.id}
      onSelectToggle={handleSelect}
      onPreview={openPreview}
      selectable
    />
  );

  const renderTemplateGrid = () => {
    if (loading && !items.length) {
      return (
        <Grid item xs={12}>
          <Box
            sx={{
              py: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CircularProgress sx={{ color: PRIMARY_COLOR, mb: 2 }} />
            <Typography variant="body2">
              {t(messages.loadingTemplates())}
            </Typography>
          </Box>
        </Grid>
      );
    }
    if (!loading && !items.length) {
      return (
        <Grid item xs={12}>
          <Box
            sx={{
              py: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t(messages.noTemplatesAvailable())}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", maxWidth: 360 }}
            >
              {t(messages.noTemplatesAvailableDescription())}
            </Typography>
          </Box>
        </Grid>
      );
    }
    return items.map((tpl) => (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={tpl.id}>
        {renderCard(tpl)}
      </Grid>
    ));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      data-testid="template-selection-modal"
      PaperProps={{
        sx: {
          height: "85vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: { xs: 2, sm: 3 },
          m: { xs: 1, sm: 3 },
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        },
      }}
      TransitionProps={{
        enter: true,
        exit: true,
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          px: 4,
          pt: 3,
          pb: 2.5,
          fontSize: 24,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {t(messages.templateSelectionTitle())}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "rgba(0,0,0,0.5)",
            "&:hover": {
              bgcolor: "rgba(0,0,0,0.04)",
            },
          }}
          size="medium"
          data-testid="template-selection-modal-close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          borderTop: "none",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
        ref={scrollParentRef}
        id="template-scroll-area"
      >
        <InfiniteScroll
          dataLength={items.length}
          next={loadMore}
          hasMore={hasMore}
          loader={
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: PRIMARY_COLOR }} />
            </Box>
          }
          scrollableTarget="template-scroll-area"
          style={{ overflow: "visible" }}
        >
          <Grid
            container
            spacing={2}
            sx={{ p: 3 }}
            data-testid="template-selection-modal-template-grid"
          >
            {renderTemplateGrid()}
          </Grid>
        </InfiniteScroll>
      </DialogContent>
      <DialogActions sx={{ p: 2, px: 3, gap: 1.5, justifyContent: "flex-end" }}>
        <SecondaryButton
          onClick={onClose}
          disabled={generating}
          sx={{
            fontWeight: 600,
            px: 2,
            py: 0.8,
            borderRadius: 1.5,
            textTransform: "none",
            fontSize: 13,
            minWidth: "80px",
            height: "32px",
          }}
          data-testid="template-selection-modal-cancel"
        >
          {t(messages.Cancel())}
        </SecondaryButton>
        {selected && (
          <SecondaryButton
            onClick={() => setSelected(undefined)}
            disabled={generating || !selected}
            sx={{
              fontWeight: 600,
              px: 2,
              py: 0.8,
              borderRadius: 1.5,
              textTransform: "none",
              fontSize: 13,
              minWidth: "80px",
              height: "32px",
            }}
            data-testid="template-selection-modal-unselect"
          >
            {t(messages.unselectButton())}
          </SecondaryButton>
        )}
        <PrimaryButton
          onClick={() => selected && onGenerate(selected)}
          disabled={!selected || generating}
          sx={{
            fontWeight: 600,
            px: 2,
            py: 0.8,
            borderRadius: 1.5,
            textTransform: "none",
            fontSize: 13,
            minWidth: "90px",
            height: "32px",
            boxShadow:
              selected && !generating ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
            "&:hover": {
              boxShadow:
                selected && !generating ? "0 4px 12px rgba(0,0,0,0.2)" : "none",
            },
          }}
          data-testid="template-selection-modal-generate"
        >
          {generating ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <CircularProgress size={14} sx={{ color: "#fff" }} />
              <span>{t(messages.generatingStatus())}</span>
            </Box>
          ) : (
            t(messages.generateButton())
          )}
        </PrimaryButton>
      </DialogActions>
      <MuiDialog
        open={previewOpen}
        onClose={closePreview}
        fullScreen
        maxWidth={false}
        data-testid="template-selection-modal-preview"
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.95)",
            position: "relative",
            backdropFilter: "blur(10px)",
            borderRadius: 0,
          },
        }}
      >
        <IconButton
          onClick={closePreview}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2,
            color: "#fff",
            bgcolor: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(4px)",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.2)",
            },
          }}
          data-testid="template-selection-modal-preview-close"
        >
          <CloseIcon />
        </IconButton>
        {previewSrc && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              zIndex: 2,
              display: "flex",
              gap: 1,
              bgcolor: "rgba(0,0,0,0.4)",
              p: 1,
              borderRadius: 2,
              backdropFilter: "blur(6px)",
            }}
          >
            <IconButton
              size="small"
              sx={{ color: "#fff" }}
              onClick={() => previewSrc && window.open(previewSrc, "_blank")}
              title={t(messages.openOriginalTooltip())}
              data-testid="template-selection-modal-preview-open-original"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 0,
            overflow: "hidden",
          }}
        >
          {previewSrc ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                background: "#000",
              }}
            >
              <img
                src={previewSrc}
                alt="Template Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
                  imageRendering: "auto",
                }}
              />
            </Box>
          ) : (
            <Typography color="#fff">{t(messages.noImageLabel())}</Typography>
          )}
        </Box>
      </MuiDialog>
    </Dialog>
  );
};

export default TemplateSelectionModal;
